/* eslint-disable no-restricted-imports */
import { PartialMessage } from '@bufbuild/protobuf'
import { ConnectError } from '@connectrpc/connect'
import { createQueryOptions, useInfiniteQuery, useQuery } from '@connectrpc/connect-query'
import {
  InfiniteData,
  UseInfiniteQueryResult,
  UseQueryResult,
  keepPreviousData,
  useQueries,
  useQuery as useTanStackQuery,
  useInfiniteQuery as useTanStackInfiniteQuery,
  skipToken,
} from '@tanstack/react-query'
import { getPosition, listPositions } from '@uniswap/client-pools/dist/pools/v1/api-PoolsService_connectquery'
import {
  GetPositionResponse,
  ListPositionsRequest,
  ListPositionsResponse,
} from '@uniswap/client-pools/dist/pools/v1/api_pb'
import { ProtocolVersion, PositionStatus } from '@uniswap/client-pools/dist/pools/v1/types_pb'
import { Pair } from '@uniswap/v2-sdk'
import { useMemo } from 'react'
import { uniswapGetTransport } from 'uniswap/src/data/rest/base'
import { SerializedToken } from 'uniswap/src/features/tokens/slice/types'
import { deserializeToken } from 'uniswap/src/utils/currency'
import { fetchPositions, ListPositionsRequest as TradingApiListPositionsRequest, ListPositionsResponse as TradingApiListPositionsResponse } from 'uniswap/src/data/apiClients/tradingApi/TradingApiClient'
import { uniswapUrls } from 'uniswap/src/constants/urls'
import { TRADING_API_CACHE_KEY } from 'uniswap/src/data/apiClients/tradingApi/TradingApiClient'

// Helper function to convert PositionStatus enum to string name
function getPositionStatusName(status: PositionStatus): string {
  const statusMap: Record<PositionStatus, string> = {
    [PositionStatus.UNSPECIFIED]: 'POSITION_STATUS_UNSPECIFIED',
    [PositionStatus.IN_RANGE]: 'POSITION_STATUS_IN_RANGE',
    [PositionStatus.OUT_OF_RANGE]: 'POSITION_STATUS_OUT_OF_RANGE',
    [PositionStatus.CLOSED]: 'POSITION_STATUS_CLOSED',
  }
  return statusMap[status] || `POSITION_STATUS_${status}`
}

// Helper function to convert ProtocolVersion enum to string name
function getProtocolVersionName(version: ProtocolVersion): string {
  const versionMap: Record<ProtocolVersion, string> = {
    [ProtocolVersion.UNSPECIFIED]: 'PROTOCOL_VERSION_UNSPECIFIED',
    [ProtocolVersion.V2]: 'PROTOCOL_VERSION_V2',
    [ProtocolVersion.V3]: 'PROTOCOL_VERSION_V3',
    [ProtocolVersion.V4]: 'PROTOCOL_VERSION_V4',
  }
  return versionMap[version] || `PROTOCOL_VERSION_${version}`
}

// Helper function to convert string enum name to PositionStatus enum
function parsePositionStatus(statusStr: string): PositionStatus {
  const statusMap: Record<string, PositionStatus> = {
    'POSITION_STATUS_UNSPECIFIED': PositionStatus.UNSPECIFIED,
    'POSITION_STATUS_IN_RANGE': PositionStatus.IN_RANGE,
    'POSITION_STATUS_OUT_OF_RANGE': PositionStatus.OUT_OF_RANGE,
    'POSITION_STATUS_CLOSED': PositionStatus.CLOSED,
  }
  return statusMap[statusStr] ?? PositionStatus.UNSPECIFIED
}

// Helper function to convert string enum name to ProtocolVersion enum
function parseProtocolVersion(versionStr: string): ProtocolVersion {
  const versionMap: Record<string, ProtocolVersion> = {
    'PROTOCOL_VERSION_UNSPECIFIED': ProtocolVersion.UNSPECIFIED,
    'PROTOCOL_VERSION_V2': ProtocolVersion.V2,
    'PROTOCOL_VERSION_V3': ProtocolVersion.V3,
    'PROTOCOL_VERSION_V4': ProtocolVersion.V4,
  }
  return versionMap[versionStr] ?? ProtocolVersion.UNSPECIFIED
}

// Helper function to convert protobuf request to plain object
function convertListPositionsRequest(input?: PartialMessage<ListPositionsRequest>): TradingApiListPositionsRequest | undefined {
  if (!input) return undefined
  
  return {
    address: input.address,
    chainIds: input.chainIds,
    positionStatuses: input.positionStatuses?.map((s: PositionStatus) => getPositionStatusName(s)),
    protocolVersions: input.protocolVersions?.map((v: ProtocolVersion) => getProtocolVersionName(v)),
    pageSize: input.pageSize,
    pageToken: input.pageToken,
  }
}

// Helper function to convert API response to protobuf format
function convertListPositionsResponse(response: TradingApiListPositionsResponse): ListPositionsResponse {
  // Convert API response positions to protobuf format
  const convertedPositions = (response.positions || []).map((apiPosition: any) => {
    // Determine position case based on available fields in API response
    // API returns v2Pair, v3Position, or v4Position fields directly
    let positionCase: 'v2Pair' | 'v3Position' | 'v4Position' | undefined
    let positionValue: any

    // First, try to find position data in the API response
    if (apiPosition.v3Position) {
      positionCase = 'v3Position'
      positionValue = apiPosition.v3Position
    } else if (apiPosition.v2Pair) {
      positionCase = 'v2Pair'
      positionValue = apiPosition.v2Pair
    } else if (apiPosition.v4Position) {
      positionCase = 'v4Position'
      positionValue = apiPosition.v4Position
    } else {
      // If no position type field is found, try to infer from protocolVersion
      // and use the entire apiPosition (minus metadata fields) as the position value
      const protocolVersionStr = apiPosition.protocolVersion
      if (protocolVersionStr === 'PROTOCOL_VERSION_V3' || protocolVersionStr === ProtocolVersion.V3) {
        positionCase = 'v3Position'
        // Use the entire apiPosition as the position value, excluding metadata fields
        const { chainId, status, protocolVersion, timestamp, ...positionData } = apiPosition
        positionValue = positionData
      } else if (protocolVersionStr === 'PROTOCOL_VERSION_V2' || protocolVersionStr === ProtocolVersion.V2) {
        positionCase = 'v2Pair'
        // Use the entire apiPosition as the position value, excluding metadata fields
        const { chainId, status, protocolVersion, timestamp, ...positionData } = apiPosition
        positionValue = positionData
      } else if (protocolVersionStr === 'PROTOCOL_VERSION_V4' || protocolVersionStr === ProtocolVersion.V4) {
        positionCase = 'v4Position'
        // Use the entire apiPosition as the position value, excluding metadata fields
        const { chainId, status, protocolVersion, timestamp, ...positionData } = apiPosition
        positionValue = positionData
      }
    }

    // Convert status string to enum
    const status = apiPosition.status 
      ? parsePositionStatus(apiPosition.status)
      : PositionStatus.UNSPECIFIED

    // Convert protocolVersion string to enum if present
    const protocolVersion = apiPosition.protocolVersion
      ? parseProtocolVersion(apiPosition.protocolVersion)
      : undefined

    // Build protobuf position structure
    // Note: timestamp is not part of the protobuf Position definition, so we exclude it
    const protobufPosition: any = {
      chainId: apiPosition.chainId,
      status,
    }

    // Add protocolVersion if present
    if (protocolVersion !== undefined) {
      protobufPosition.protocolVersion = protocolVersion
    }

    // Add position case and value - for protobuf oneof fields, fromJson expects the field name directly
    // It will automatically convert it to position: { case: 'v3Position', value: ... } structure
    if (positionCase && positionValue) {
      // For protobuf oneof fields, fromJson expects the field name directly (e.g., v3Position, v2Pair, v4Position)
      protobufPosition[positionCase] = positionValue
    } else {
      // If we can't determine the position type, log a warning and skip this position
      console.warn('Failed to determine position type for position:', {
        chainId: apiPosition.chainId,
        protocolVersion: apiPosition.protocolVersion,
        availableKeys: Object.keys(apiPosition),
      })
      // Return null to filter out invalid positions
      return null
    }

    return protobufPosition
  }).filter((pos): pos is any => pos !== null) // Filter out null positions

  return ListPositionsResponse.fromJson(
    {
      positions: convertedPositions,
      pageToken: response.pageToken || '',
    } as any,
    { ignoreUnknownFields: true }
  )
}

export function useGetPositionsQuery(
  input?: PartialMessage<ListPositionsRequest>,
  disabled?: boolean,
): UseQueryResult<ListPositionsResponse, ConnectError> {
  const request = convertListPositionsRequest(input)
  const queryKey = [TRADING_API_CACHE_KEY, uniswapUrls.tradingApiPaths.positions, request]
  
  return useTanStackQuery({
    queryKey,
    queryFn: request && !disabled
      ? async (): Promise<ListPositionsResponse> => {
          const response = await fetchPositions(request)
          return convertListPositionsResponse(response)
        }
      : skipToken,
    enabled: !!request && !disabled,
    placeholderData: keepPreviousData,
  }) as UseQueryResult<ListPositionsResponse, ConnectError>
}

export function useGetPositionsInfiniteQuery(
  input: PartialMessage<ListPositionsRequest> & { pageToken: string },
  disabled?: boolean,
): UseInfiniteQueryResult<InfiniteData<ListPositionsResponse>, ConnectError> {
  const request = convertListPositionsRequest(input)
  const queryKey = [TRADING_API_CACHE_KEY, uniswapUrls.tradingApiPaths.positions, request]
  
  return useTanStackInfiniteQuery({
    queryKey,
    queryFn: request && !disabled
      ? async ({ pageParam = input.pageToken || '' }): Promise<ListPositionsResponse> => {
          const requestParams = { ...request, pageToken: pageParam }
          const response = await fetchPositions(requestParams)
          return convertListPositionsResponse(response)
        }
      : skipToken,
    enabled: !!request && !disabled,
    initialPageParam: input.pageToken || '',
    getNextPageParam: (lastPage) => {
      const pageToken = (lastPage as any).pageToken
      return pageToken || undefined
    },
    placeholderData: keepPreviousData,
  }) as UseInfiniteQueryResult<InfiniteData<ListPositionsResponse>, ConnectError>
}

export function useGetPositionsForPairs(
  serializedPairs: {
    [chainId: number]: {
      [key: string]: { token0: SerializedToken; token1: SerializedToken }
    }
  },
  account?: Address,
): UseQueryResult<GetPositionResponse, ConnectError>[] {
  const positionsQueryOptions = useMemo(() => {
    return Object.keys(serializedPairs || {})
      .flatMap((chainId) => {
        const pairsForChain = serializedPairs[Number(chainId)]
        if (!pairsForChain) {
          return []
        }
        return Object.keys(pairsForChain).map((pairId) => {
          const pair = pairsForChain[pairId]
          if (!pair) {
            return undefined
          }
          const [token0, token1] = [deserializeToken(pair.token0), deserializeToken(pair.token1)]
          const pairAddress = Pair.getAddress(token0, token1)
          return createQueryOptions(
            getPosition,
            account
              ? {
                  chainId: Number(chainId),
                  protocolVersion: ProtocolVersion.V2,
                  pairAddress,
                  owner: account,
                }
              : undefined,
            { transport: uniswapGetTransport },
          )
        })
      })
      .filter(isDefined)
  }, [serializedPairs, account])

  return useQueries({
    queries: positionsQueryOptions,
  })
}

function isDefined<T>(value: T | undefined): value is T {
  return value !== undefined
}
